import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Reveal, Eyebrow } from "../components/Reveal";
import { StatusBadge } from "../components/StatusBadge";
import { TicketButton } from "../components/TicketButton";
import { cad, fullDate, showDateTime, time12 } from "../lib/format";
import { imgSrc } from "../lib/api";

const useCountdown = (target) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, (target?.getTime?.() ?? 0) - now);
  return [
    ["Days", Math.floor(diff / 864e5)],
    ["Hrs", Math.floor(diff / 36e5) % 24],
    ["Min", Math.floor(diff / 6e4) % 60],
    ["Sec", Math.floor(diff / 1e3) % 60],
  ];
};

export const NextConcert = ({ concert: c }) => {
  const units = useCountdown(c ? showDateTime(c) : null);
  if (!c) return null;
  return (
    <section id="next-show" data-testid="next-concert-section" className="relative mx-auto max-w-7xl px-5 py-20 lg:px-12 lg:py-28">
      <Reveal>
        <div className="grid overflow-hidden rounded-[28px] border border-white/10 bg-[#12141C] lg:grid-cols-12">
          <div className="relative min-h-[300px] lg:col-span-7 lg:min-h-[520px]">
            <img src={imgSrc(c.image)} alt={`${c.artist} live`} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#12141C] via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-[#12141C]" />
            <div className="clip-tag absolute left-0 top-8 bg-sunset px-5 py-2 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-black">Next Up</div>
          </div>
          <div className="flex flex-col justify-center gap-6 p-7 sm:p-10 lg:col-span-5">
            <Eyebrow>{c.genre}</Eyebrow>
            <h2 data-testid="next-concert-artist" className="font-display text-5xl font-extrabold uppercase leading-[0.9] text-white sm:text-6xl">{c.artist}</h2>
            <p className="text-zinc-300">{fullDate(c.date)} · Doors {time12(c.doors_time)} · Show {time12(c.show_time)}</p>
            <div data-testid="next-concert-countdown" className="grid grid-cols-4 gap-2" aria-label="Countdown to show time">
              {units.map(([label, v]) => (
                <div key={label} className="rounded-2xl border border-white/10 bg-[#0B0C10] py-3 text-center">
                  <div className="font-display text-3xl font-extrabold tabular-nums text-white sm:text-4xl">{String(v).padStart(2, "0")}</div>
                  <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">{label}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <StatusBadge status={c.status} testId="next-concert-status" />
              {c.price_from != null && <span className="font-mono text-sm text-zinc-300">From {cad(c.price_from)}</span>}
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <TicketButton concert={c} testId="next-concert-tickets-btn" className="btn-lg justify-center" />
              <Link to={`/concerts/${c.slug}`} data-testid="next-concert-details-btn" className="btn-ghost btn-lg justify-center">View Details</Link>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
};
